import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/outline";
import { useSearchParams } from "react-router-dom";

import { Button } from "@/components/ui/button";

/**
 * Landing page shown in the popup opened by the Test SSO button.
 * Reads the outcome from `?test_result=…&email=…&reason=…` set by the
 * SSO callback. Self-contained because the popup is `noopener` and
 * cannot be closed programmatically.
 */
export const SsoTestResultPage = () => {
  const [searchParams] = useSearchParams();
  const result = searchParams.get("test_result");
  const email = searchParams.get("email");
  const reason = searchParams.get("reason");
  const succeeded = result === "succeeded";

  return (
    <div className="flex flex-col items-center text-center">
      <div
        className={`mb-6 flex h-16 w-16 items-center justify-center rounded-full ${
          succeeded ? "bg-green-50" : "bg-red-50"
        }`}
      >
        {succeeded ? (
          <CheckCircleIcon className="h-8 w-8 text-green-600" strokeWidth={2} />
        ) : (
          <XCircleIcon className="h-8 w-8 text-red-600" strokeWidth={2} />
        )}
      </div>

      <h1 className="mb-2 text-2xl font-bold text-gray-900 sm:text-3xl">
        {succeeded ? "Test SSO succeeded" : "Test SSO failed"}
      </h1>

      <p className="mb-6 text-base text-gray-600">
        {succeeded
          ? "The round-trip to your identity provider completed without errors."
          : "We could not complete the SSO round-trip. See details below."}
      </p>

      {(email || reason) && (
        <div className="mb-8 w-full rounded-lg border border-gray-200 bg-gray-50 p-4 text-left">
          {email && (
            <>
              <p className="mb-1 text-sm font-semibold text-gray-900">Profile email resolved</p>
              <p className="mb-3 break-all text-sm text-gray-600">{email}</p>
            </>
          )}
          {reason && (
            <>
              <p className="mb-1 text-sm font-semibold text-gray-900">Failure reason</p>
              <p className="break-all text-sm text-gray-600">{reason}</p>
            </>
          )}
        </div>
      )}

      <p className="mb-6 text-sm text-gray-500">
        Return to the original tab — the Audit log on your Enterprise Identity settings page now
        shows {succeeded ? "an `sso_test_succeeded`" : "an `sso_test_failed`"} row with the full
        WorkOS request id.
      </p>

      <div className="w-full">
        <Button variant="outline" className="w-full" onClick={() => window.close()}>
          Close this tab
        </Button>
      </div>
    </div>
  );
};
